// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import {
  ArrowRight,
  Check,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  Database,
  FileWarning,
  FolderInput,
  FolderOpen,
  FolderOutput,
  Info,
  LoaderCircle,
} from 'lucide-react';
import type {
  ExportJobSettings,
  WatchedFolderDetails,
  WatchedFolderSetting,
  WatchedFolderStatus,
} from '../shared/desktop-api';
import { folderExportHold } from './export-next-step';
import { JobIdentity } from './JobIdentity';
import { TooltipButton } from './TooltipButton';
import { shortDateTime } from './short-date-time';
import {
  folderName,
  messageOf,
  plainRefusal,
  plainSetAsideReason,
  watchedLook,
  type PlainSentence,
} from './watched-folder-words';
import './export-route.css';
import './watched-folder.css';

/**
 * Job setup › Watched folder (ADR-0090 decision 10). The idea is drawn, not explained: the folder,
 * this job and the export folder as the same route Export uses, with dots travelling while files are
 * taken. Under it, the one folder and what is happening in it now. A choice saves at once: there is
 * nothing to Save, as in Lookup lists. Every sentence the coordinator refuses with is shown short,
 * with its own words a click away; nothing here opens a Windows message box.
 */
export function WatchedFolderSection({
  jobId,
  jobName,
  onOpenExport,
}: {
  jobId: string;
  jobName: string;
  onOpenExport: () => void;
}) {
  const api = window.capturePointDesktop;
  const titleId = useId();
  const [setting, setSetting] = useState<WatchedFolderSetting | null | undefined>(undefined);
  const [status, setStatus] = useState<WatchedFolderStatus | null>(null);
  const [details, setDetails] = useState<WatchedFolderDetails | null>(null);
  const [exportTo, setExportTo] = useState<
    { name: string; kind: 'folder' | 'content-central' } | null | undefined
  >(undefined);
  // VM rehearsal D12: the engine's sentence when the automatic export cannot use its folder (Export shows the same).
  const [exportHold, setExportHold] = useState<string | null>(null);
  const [busy, setBusy] = useState<
    'choose' | 'test' | 'toggle' | 'stop' | 'open' | 'separation' | 'grant' | null
  >(null);
  const [notice, setNotice] = useState<(PlainSentence & { tone: 'ok' | 'problem' }) | null>(null);
  const [loadFailure, setLoadFailure] = useState<string | null>(null);
  const [reasonOpen, setReasonOpen] = useState(false);
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  const readDetails = useCallback(() => {
    void api
      ?.watchedFolderDetails(jobId)
      .then((next) => {
        if (live.current) setDetails(next);
      })
      .catch(() => undefined);
  }, [api, jobId]);
  const read = useCallback(() => {
    if (!api?.getWatchedFolder) {
      setLoadFailure("Watched folders aren't available here.");
      return;
    }
    setLoadFailure(null);
    void Promise.all([api.getWatchedFolder(jobId), api.watchedFolderStatus()])
      .then(([saved, all]) => {
        if (!live.current) return;
        setSetting(saved);
        setStatus(all.find((entry) => entry.jobId === jobId) ?? null);
      })
      .catch((reason) => {
        if (live.current) setLoadFailure(plainRefusal(messageOf(reason)).text);
      });
    readDetails();
    void api
      .getExportSettings(jobId)
      .then((settings: ExportJobSettings | null) => {
        if (!live.current) return;
        if (settings?.destinationKind === 'content-central')
          setExportTo({ name: 'Content Central', kind: 'content-central' });
        else
          setExportTo(
            settings?.destinationRoot
              ? { name: folderName(settings.destinationRoot), kind: 'folder' }
              : null,
          );
        setExportHold(folderExportHold(settings));
      })
      .catch(() => {
        if (live.current) {
          setExportTo(null);
          setExportHold(null);
        }
      });
  }, [api, jobId, readDetails]);
  useEffect(() => {
    setSetting(undefined);
    setNotice(null);
    read();
  }, [read]);
  // Live: the coordinator says when any folder's status changes; set-aside files are looked at again
  // when a file is set aside, and every half minute while this section is open.
  const lastFailureAt = useRef<string | null>(null);
  useEffect(
    () =>
      api?.onWatchedFolderStatusChanged((all) => {
        const mine = all.find((entry) => entry.jobId === jobId) ?? null;
        setStatus(mine);
        if ((mine?.lastFailure?.at ?? null) !== lastFailureAt.current) {
          lastFailureAt.current = mine?.lastFailure?.at ?? null;
          readDetails();
        }
      }),
    [api, jobId, readDetails],
  );
  useEffect(() => {
    const timer = window.setInterval(() => {
      readDetails();
      void api
        ?.watchedFolderStatus()
        .then((all) => {
          if (live.current) setStatus(all.find((entry) => entry.jobId === jobId) ?? null);
        })
        .catch(() => undefined);
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [api, jobId, readDetails]);

  const act = async (
    kind: NonNullable<typeof busy>,
    work: () => Promise<void>,
    folder?: string,
    choosing = false,
  ) => {
    if (busy) return;
    setBusy(kind);
    setNotice(null);
    try {
      await work();
    } catch (reason) {
      if (live.current)
        setNotice({ ...plainRefusal(messageOf(reason), folder, choosing), tone: 'problem' });
    } finally {
      if (live.current) setBusy(null);
    }
  };
  const choose = () =>
    act('choose', async () => {
      const chosen = await api!.chooseWatchedFolder(jobId);
      if (!chosen) return;
      try {
        setSetting(await api!.setWatchedFolder(jobId, chosen));
      } catch (reason) {
        setNotice({ ...plainRefusal(messageOf(reason), chosen, true), tone: 'problem' });
        return;
      }
      setReasonOpen(false);
      read();
    });
  const test = () =>
    setting &&
    act(
      'test',
      async () => {
        const result = await api!.testWatchedFolder(setting.path);
        if (!live.current) return;
        setNotice(
          result.usable
            ? { text: 'Ready to use.', detail: null, tone: 'ok' }
            : { ...plainRefusal(result.summary, setting.path), tone: 'problem' },
        );
      },
      setting.path,
    );
  const toggle = (enabled: boolean) =>
    setting &&
    act(
      'toggle',
      async () => {
        setSetting(await api!.setWatchedFolderEnabled(jobId, enabled));
        read();
      },
      setting.path,
    );
  // ADR-0091: what arrives here. Scan stacks are split by Automatic; one document per file keeps each file whole.
  const separate = (separation: 'whole' | 'stack') =>
    setting &&
    act(
      'separation',
      async () => {
        setSetting(await api!.setWatchedFolderSeparation(jobId, separation));
      },
      setting.path,
    );
  const stop = () =>
    act('stop', async () => {
      await api!.setWatchedFolder(jobId, null);
      setSetting(null);
      setStatus(null);
      read();
    });
  // California Screw, 2026-10-15: the service may not move files others put here. Choosing the same
  // folder again lets this window ask Windows once to give the service access to it, then tests it.
  const grant = () =>
    setting &&
    act(
      'grant',
      async () => {
        setSetting(await api!.setWatchedFolder(jobId, setting.path));
        if (live.current)
          setNotice({ text: 'Paige can take files here now.', detail: null, tone: 'ok' });
        read();
      },
      setting.path,
      true,
    );
  const open = (which: 'folder' | 'set-aside') =>
    act('open', () => api!.openWatchedFolder(jobId, which), setting?.path);

  const enabled = setting?.enabled ?? false;
  const look = watchedLook(status, enabled);
  const setAside = details?.setAside;
  const flowing = Boolean(setting) && look.tone === 'on';
  const stopCard = (
    label: string,
    value: ReactNode,
    icon: ReactNode,
    extra?: { onClick?: () => void; className?: string; title?: string; aria?: string },
  ) => {
    const body = (
      <>
        <span className="export-route-icon" aria-hidden="true">
          {icon}
        </span>
        <span className="export-route-text">
          <small>{label}</small>
          <b>{value}</b>
        </span>
      </>
    );
    return extra?.onClick ? (
      <button
        type="button"
        className={`export-route-stop${extra.className ? ` ${extra.className}` : ''}`}
        title={extra.title}
        aria-label={extra.aria}
        onClick={extra.onClick}
      >
        {body}
      </button>
    ) : (
      <div
        className={`export-route-stop watched-route-fact${extra?.className ? ` ${extra.className}` : ''}`}
        title={extra?.title}
      >
        {body}
      </div>
    );
  };
  const exportStop =
    exportTo === undefined
      ? stopCard(
          'Export folder',
          <LoaderCircle className="spin" size={14} aria-label="Loading" />,
          <FolderOutput size={19} />,
        )
      : exportTo
        ? stopCard(
            exportTo.kind === 'folder' ? 'Export folder' : 'Exports to',
            exportTo.name,
            exportTo.kind === 'folder' ? <FolderOutput size={19} /> : <Database size={19} />,
            { onClick: onOpenExport, aria: `Export: ${exportTo.name}` },
          )
        : stopCard(
            'Export folder',
            <span className="watched-route-unset">
              Not set up
              <span className="watched-route-go">
                Set up
                <ArrowRight size={13} aria-hidden="true" />
              </span>
            </span>,
            <FolderOutput size={19} />,
            {
              onClick: onOpenExport,
              className: 'is-unset',
              aria: 'Export folder not set up. Set up export',
            },
          );

  return (
    <section
      className="export-route-card watched-folder-card"
      aria-labelledby={titleId}
      data-telemetry-area="watched-folder"
    >
      <div className="export-head">
        <h2 id={titleId}>
          <FolderInput size={22} aria-hidden="true" />
          Watched folder
        </h2>
        {setting && (
          <div className="export-head-actions">
            {setAside && setAside.count > 0 && (
              <button
                type="button"
                className="watched-pill is-wait"
                onClick={() =>
                  document
                    .getElementById(`${titleId}-set-aside`)
                    ?.scrollIntoView({ block: 'nearest' })
                }
              >
                <FileWarning size={14} aria-hidden="true" />
                {setAside.count} set aside
              </button>
            )}
            <button
              type="button"
              className={`export-status watched-status is-${look.tone}`}
              aria-expanded={look.reason ? reasonOpen : undefined}
              disabled={!look.reason}
              title={look.reason ?? undefined}
              onClick={() => setReasonOpen((open) => !open)}
            >
              <i aria-hidden="true" />
              <span role="status">{look.label}</span>
              {look.reason &&
                (reasonOpen ? (
                  <ChevronDown size={15} aria-hidden="true" />
                ) : (
                  <ChevronRight size={15} aria-hidden="true" />
                ))}
            </button>
            <label className="watched-switch" title={enabled ? 'Pause' : 'Resume'}>
              <input
                type="checkbox"
                role="switch"
                className="export-switch"
                aria-label="Watch this folder"
                checked={enabled}
                disabled={busy !== null}
                onChange={(event) => void toggle(event.target.checked)}
              />
            </label>
          </div>
        )}
      </div>
      {setting && reasonOpen && look.reason && (
        <p className="watched-reason" role="note">
          <Info size={14} aria-hidden="true" />
          {look.reason}
          {status?.cannotTake && details?.runsAs === 'service' && (
            <button
              type="button"
              className="quiet-button small"
              data-telemetry="watched-grant"
              disabled={busy !== null}
              onClick={() => void grant()}
            >
              {busy === 'grant' ? (
                <LoaderCircle className="spin" size={13} aria-hidden="true" />
              ) : null}
              Give Paige access
              <ArrowRight size={13} aria-hidden="true" />
            </button>
          )}
        </p>
      )}

      <div
        className={`export-route watched-route${setting ? '' : ' is-empty'}`}
        role="group"
        aria-label="Where files go"
      >
        <div className="export-route-line">
          <div className="export-route-track" aria-hidden="true">
            {flowing && (
              <>
                <i className="export-route-flow" />
                <i className="export-route-flow" style={{ animationDelay: '-2.5s' }} />
                <i className="export-route-flow" style={{ animationDelay: '-5s' }} />
              </>
            )}
          </div>
          {setting
            ? stopCard('Watched folder', folderName(setting.path), <FolderInput size={19} />, {
                onClick: () => void open('folder'),
                className: `selected is-${look.tone}`,
                title: setting.path,
                aria: `Open ${setting.path}`,
              })
            : stopCard('Watched folder', 'None yet', <FolderInput size={19} />, {
                className: 'is-unset',
              })}
          <ChevronRight className="export-route-arrow" size={15} aria-hidden="true" />
          {stopCard('This job', jobName, <JobIdentity jobId={jobId} />, {
            className: 'watched-route-job',
          })}
          <ChevronRight className="export-route-arrow" size={15} aria-hidden="true" />
          {exportStop}
        </div>
      </div>
      {exportHold && (
        <p className="watched-notice is-problem" role="alert" data-telemetry="watched-export-hold">
          <CircleAlert size={16} aria-hidden="true" />
          <span>{exportHold}</span>
          <button type="button" className="quiet-button small" onClick={onOpenExport}>
            Export settings
            <ArrowRight size={13} aria-hidden="true" />
          </button>
        </p>
      )}

      {setting === undefined ? (
        loadFailure ? (
          <p className="watched-notice is-problem" role="alert">
            <CircleAlert size={16} aria-hidden="true" />
            <span>{loadFailure}</span>
            <button type="button" className="quiet-button small" onClick={read}>
              Try again
            </button>
          </p>
        ) : (
          <p className="watched-loading" role="status">
            <LoaderCircle className="spin" size={16} aria-hidden="true" />
            Loading…
          </p>
        )
      ) : !setting ? (
        <div className="export-stage watched-empty">
          <WatchedFolderArt />
          <p>Files dropped in a folder become documents in this job.</p>
          <button
            type="button"
            className="primary-button watched-choose"
            data-telemetry="watched-choose"
            disabled={busy !== null}
            onClick={() => void choose()}
          >
            {busy === 'choose' ? (
              <LoaderCircle className="spin" size={16} aria-hidden="true" />
            ) : (
              <FolderOpen size={16} aria-hidden="true" />
            )}
            Choose a folder
          </button>
          {notice && <Notice notice={notice} />}
          {details?.runsAs === 'application' && <RunsWhileOpen />}
        </div>
      ) : (
        <div className="export-stage watched-stage">
          <div className="watched-folder-row">
            <button
              type="button"
              className="watched-path"
              data-telemetry="watched-open"
              title="Open folder"
              onClick={() => void open('folder')}
            >
              <FolderOpen size={16} aria-hidden="true" />
              <span>{setting.path}</span>
            </button>
            <div className="watched-actions">
              <button
                type="button"
                className="secondary-button small"
                data-telemetry="watched-test"
                disabled={busy !== null}
                onClick={() => void test()}
              >
                {busy === 'test' ? (
                  <LoaderCircle className="spin" size={14} aria-hidden="true" />
                ) : (
                  <Check size={14} aria-hidden="true" />
                )}
                Test
              </button>
              <button
                type="button"
                className="secondary-button small"
                data-telemetry="watched-change"
                disabled={busy !== null}
                onClick={() => void choose()}
              >
                {busy === 'choose' ? 'Choosing…' : 'Change'}
              </button>
              <button
                type="button"
                className="quiet-button small watched-stop"
                data-telemetry="watched-stop"
                disabled={busy !== null}
                onClick={() => void stop()}
              >
                Stop watching
              </button>
            </div>
          </div>
          <fieldset className="watched-separation" disabled={busy !== null}>
            <legend>Files here are</legend>
            {(
              [
                ['stack', 'Scan stacks'],
                ['whole', 'One document per file'],
              ] as const
            ).map(([value, label]) => (
              <label
                key={value}
                className={(setting.separation ?? 'stack') === value ? 'selected' : undefined}
              >
                <input
                  type="radio"
                  name={`${titleId}-separation`}
                  value={value}
                  checked={(setting.separation ?? 'stack') === value}
                  data-telemetry={
                    value === 'stack' ? 'watched-separation-stack' : 'watched-separation-whole'
                  }
                  onChange={() => void separate(value)}
                />
                {label}
              </label>
            ))}
          </fieldset>
          {notice && <Notice notice={notice} />}
          <div className="watched-facts">
            <Fact label="Waiting" value={(status?.waitingFiles ?? 0).toLocaleString()} />
            <Fact
              label="Last file"
              value={status?.lastTaken?.name ?? 'None yet'}
              sub={status?.lastTaken ? shortDateTime(status.lastTaken.at) : undefined}
            />
            <Fact
              label="Set aside"
              value={(setAside?.count ?? 0).toLocaleString()}
              tone={setAside && setAside.count > 0 ? 'wait' : undefined}
              action={
                setAside && setAside.count > 0 ? (
                  <button
                    type="button"
                    className="quiet-button small"
                    data-telemetry="watched-open-set-aside"
                    onClick={() => void open('set-aside')}
                  >
                    Open
                    <ArrowRight size={13} aria-hidden="true" />
                  </button>
                ) : undefined
              }
            />
          </div>
          {setAside && setAside.count > 0 && (
            <ul
              className="watched-set-aside"
              id={`${titleId}-set-aside`}
              aria-label="Files set aside"
            >
              {setAside.files.slice(0, 5).map((file) => (
                <li key={file.name}>
                  <FileWarning size={15} aria-hidden="true" />
                  <b title={file.name}>{file.name}</b>
                  <span title={file.reason ?? undefined}>{plainSetAsideReason(file.reason)}</span>
                </li>
              ))}
              {setAside.count > 5 && (
                <li className="watched-more">+{(setAside.count - 5).toLocaleString()} more</li>
              )}
            </ul>
          )}
          {details?.runsAs === 'application' && <RunsWhileOpen />}
        </div>
      )}
    </section>
  );
}

function Notice({ notice }: { notice: PlainSentence & { tone: 'ok' | 'problem' } }) {
  return (
    <div
      className={`watched-notice is-${notice.tone}`}
      role={notice.tone === 'problem' ? 'alert' : 'status'}
    >
      {notice.tone === 'ok' ? (
        <Check size={16} aria-hidden="true" />
      ) : (
        <CircleAlert size={16} aria-hidden="true" />
      )}
      <span>{notice.text}</span>
      {notice.detail && (
        <details className="watched-detail">
          <summary>Why?</summary>
          <p>{notice.detail}</p>
        </details>
      )}
    </div>
  );
}

function Fact({
  label,
  value,
  sub,
  tone,
  action,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'wait';
  action?: ReactNode;
}) {
  return (
    <div className={`watched-fact${tone ? ` is-${tone}` : ''}`}>
      <small>{label}</small>
      <b title={value}>{value}</b>
      {sub && <span>{sub}</span>}
      {action}
    </div>
  );
}

/** Pages dropping into a folder and coming out as one document: the idea, drawn. They fall only while the pointer is here. */
function WatchedFolderArt() {
  return (
    <svg className="watched-art" viewBox="0 0 260 120" aria-hidden="true">
      <g className="watched-art-drop">
        <rect className="watched-art-sheet" x="52" y="6" width="30" height="38" rx="3" />
        <path d="M58 16h18M58 23h18M58 30h12" />
      </g>
      <g className="watched-art-drop is-late">
        <rect className="watched-art-sheet" x="74" y="0" width="30" height="38" rx="3" />
        <path d="M80 10h18M80 17h18M80 24h12" />
      </g>
      <path
        className="watched-art-folder"
        d="M30 54h30l8 9h52a6 6 0 0 1 6 6v39a6 6 0 0 1-6 6H36a6 6 0 0 1-6-6V54z"
      />
      <path className="watched-art-arrow" d="M142 86h46m-8-7 8 7-8 7" />
      <g className="watched-art-doc">
        <rect className="watched-art-sheet is-done" x="200" y="56" width="40" height="52" rx="4" />
        <path d="M208 70h24M208 79h24M208 88h16" />
        <circle cx="232" cy="100" r="7" />
        <path d="m229 100 2 2 4-4" />
      </g>
    </svg>
  );
}

/** The per-person Paige takes files only while it runs; the service, always. */
function RunsWhileOpen() {
  return (
    <p className="watched-runs-while-open">
      <TooltipButton
        type="button"
        className="watched-info"
        tooltip="Run Paige as a Windows service to watch all the time."
        aria-label="About watching"
      >
        <Info size={14} />
      </TooltipButton>
      Watches while Paige is open.
    </p>
  );
}
